import styled from "@emotion/styled";
import { Box, Typography, Stack, Button, Skeleton, Switch, Tooltip, IconButton, Checkbox, FormControlLabel } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { AlarmCardsContent } from "../components/AlarmCardsContent";
import InfoIcon from '@mui/icons-material/Info';
import { reportError } from "utils/reportError";

import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TabNavigation from "../components/TabNavigation";
import VerticalTabs from "../components/VerticalTabs";
import MuiDateRangePicker from "views/common/DateRangePicker";
import {
    useGetAvailabilityQuery, useGetCountDataQuery, useLazyGetSuDataQuery,
    useGetOutageDataQuery,
    useGetMaintenanceMessageQuery,
} from "api/homeApi";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedDates, setVolteCallsCount, setAutoRefresh } from "store/slice/HomeSlice";
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { extractVoLTECalls, extractVoLTEFromObject, formatDateTimeForDBQuery, getDateRangeDataTransformResponse, getNewLiveDataTransformResponse } from "utils/AppUtil";
import OutagesContent from "components/OutagesContent";
import { useLayoutPreferences } from "components/LayoutPreferencesProvider";

// ── Maintenance window helpers (all times EST) ──────────────────────────────
function getOrdinalSuffix(n) {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
}

function getUpcomingWednesdayLabel() {
    const now = new Date();
    // Convert to EST
    const estNow = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const day = estNow.getDay(); // 0=Sun … 6=Sat
    const daysUntilWed = day <= 3 ? 3 - day : 10 - day; // this week or next week
    const wed = new Date(estNow);
    wed.setDate(estNow.getDate() + daysUntilWed);
    const month = wed.toLocaleString("en-US", { month: "long", timeZone: "America/New_York" });
    const d = wed.getDate();
    const year = wed.getFullYear();
    return `${month} ${d}${getOrdinalSuffix(d)}, ${year}`;
}

// eslint-disable-next-line no-unused-vars
function isMaintenanceWindow() {
    // Kept as a local fallback reference — banner visibility is driven by the API (maintenanceData.active)
    const now = new Date();
    const estNow = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const day = estNow.getDay();
    const hours = estNow.getHours();
    if (day === 1 || day === 2) return true;
    if (day === 3 && hours < 9) return true;
    return false;
}

// Helper function to format time from HH:MM format to 12-hour format with AM/PM
function formatTime24to12(timeStr) {
    if (!timeStr) return "TBD";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours, 10);
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const period = hour < 12 ? "AM" : "PM";
    return `${displayHour.toString().padStart(2, "0")}:${minutes} ${period}`;
}

// Helper function to check if a date is from the current week (Monday-Friday)
function isDateInCurrentWeek(dateStr) {
    if (!dateStr) return false;
    const now = new Date();
    const estNow = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const messageDate = new Date(dateStr);
    const estMessageDate = new Date(messageDate.toLocaleString("en-US", { timeZone: "America/New_York" }));
    
    // Get Monday of current week
    const currentDay = estNow.getDay();
    const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1; // 0=Sunday, 1=Monday
    const monday = new Date(estNow);
    monday.setDate(estNow.getDate() - daysFromMonday);
    monday.setHours(0, 0, 0, 0);
    
    // Get Friday of current week
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    friday.setHours(23, 59, 59, 999);
    
    return estMessageDate >= monday && estMessageDate <= friday;
}
// ────────────────────────────────────────────────────────────────────────────


const SelectTodayButton = styled(Button)(({ active }) => ({
    backgroundColor: active ? '#0a2240' : 'white',
    color: active ? 'white' : '#005AA6',
    fontWeight: 600,
    textTransform: 'none',
    padding: '8px 16px',
    height: '40px',
    borderRadius: '4px 0 0 4px',
    border: '1px solid #0000001F',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    minWidth: 'auto',
    '&:hover': {
        backgroundColor: active ? '#0a2240' : 'white',
    }
}));

// Create a loading skeleton component for alarm cards
const AlarmCardsLoadingSkeleton = () => (
    <Box sx={{ padding: { xs: '1rem', sm: '1.3vw' } }}>
        <Stack spacing={2}>
            <Skeleton variant="rectangular" height={90} />
            <Skeleton variant="rectangular" height={90} />
        </Stack>
    </Box>
);

// Create loading skeleton for table/vertical tabs
const TableLoadingSkeleton = () => (
    <Box sx={{ padding: '1.3vw' }}>
        <Stack spacing={2}>
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={200} />
            <Skeleton variant="rectangular" height={200} />
            <Skeleton variant="rectangular" height={200} />
        </Stack>
    </Box>
);

const TAB_FEATURE_KEYS = { alarms: "alarms", outages: "outages", userCounts: "user_counts" };

export const Dashboard = ({ user, features, onNavigate }) => {
    const dispatch = useDispatch();
    const { isEditing, homeDraft, toggleHomeKeys, filterFeatures } = useLayoutPreferences();
    const allowedFeatures = useMemo(() => (Array.isArray(features) ? features : []), [features]);
    // While customizing, every allowed feature is shown so it can be re-enabled.
    const displayedFeatures = useMemo(
        () => (isEditing ? allowedFeatures : filterFeatures(allowedFeatures)),
        [isEditing, allowedFeatures, filterFeatures]
    );
    const hasFeature = (key) => displayedFeatures.some(f => f.key === key);
    const featureSectionSx = (key) => (
        isEditing && !homeDraft.has(key) ? { opacity: 0.35, transition: "opacity 0.2s ease" } : { transition: "opacity 0.2s ease" }
    );
    // const navigate = useNavigate();
    const [tableData, setTableData] = useState([]);
    const cardsRef = useRef(null);
    const outagesRef = useRef(null);
    const tableRef = useRef(null);
    const [activeTab, setActiveTab] = useState("alarms");
    const [dateSelectionMode, setDateSelectionMode] = useState('today');
    const [comparisonEnabled, setComparisonEnabled] = useState(false);
    const [comparisonDates, setComparisonDates] = useState(null);
    const [comparisonTableData, setComparisonTableData] = useState([]);
    const [comparisonVolteCallsCount, setComparisonVolteCallsCount] = useState(null);
    const autoRefreshIntervalRef = useRef(null);

    // const onNavigate = (route) => {
    //     navigate(route);
    // }

    // const [getSuDataWithDates, { data: suData, isFetching: isLoadingSuData, isSuccess: suDataSuccess }] = useLazyGetSuDataWithDateFilterQuery();
    // const [getSuDataDaily, { data: suDataDaily, isFetching: suDataDailyLoading, isSuccess: suDataDailySuccess }] = useLazyGetSuDataQuery();
    const [getData, { data: apiData, isFetching: isLoadingData, isSuccess: dataSuccess }] = useLazyGetSuDataQuery();
    const [getComparisonData, {
        data: comparisonApiData,
        isFetching: isLoadingComparisonData,
        isSuccess: comparisonDataSuccess,
        isError: comparisonDataError,
    }] = useLazyGetSuDataQuery();
    const [selectedVerticleTab, setSelectedVerticleTab] = useState(0);

    useEffect(() => {
        const tabKeys = Object.keys(TAB_FEATURE_KEYS).filter(tab => displayedFeatures.some(f => f.key === TAB_FEATURE_KEYS[tab]));
        if (tabKeys.length > 0 && !tabKeys.includes(activeTab)) {
            setActiveTab(tabKeys[0]);
        }
    }, [displayedFeatures, activeTab]);
    // console.log("selectted verticle tab", selectedVerticleTab);

    const { selectedDates, volteCallsCount, autoRefresh } = useSelector((state) => state.home);
    console.log('selectedDates', selectedDates);

    // SET DEFAULT "TODAY" ON FIRST LOAD
    useEffect(() => {
        if (!selectedDates) {
            const today = {
                start: { _d: new Date() },
                end: { _d: new Date() }
            };

            console.log("today", today)
            dispatch(setSelectedDates(today));
        }
    }, [selectedDates, dispatch]);

    // MAIN DATA FETCH LOGIC (tab + date mode)
    useEffect(() => {
        const site = ['su', 'caesars', '5g'][selectedVerticleTab] ?? 'su';

        if (dateSelectionMode === 'today') {
            getData({ site });
            // getCSVData({ site });
        } else if (
            selectedDates?.start?._d &&
            selectedDates?.end?._d &&
            dateSelectionMode === 'range'
        ) {
            const dbDates = formatDateTimeForDBQuery(selectedDates, selectedVerticleTab);
            getData({
                site,
                start: dbDates.start,
                end: dbDates.end
            });
        }
    }, [selectedVerticleTab, dateSelectionMode, selectedDates, getData]);
    useEffect(() => {
        if (dataSuccess && !isLoadingData) {
            try {
                let transformedData;
                let volteValue = null;

                if (dateSelectionMode === 'today') {
                    // TODAY → live flat payload (array format)
                    transformedData = getNewLiveDataTransformResponse(apiData);

                    // Extract VoLTE from array
                    volteValue = extractVoLTECalls(apiData);

                    console.log('✅ TODAY transformedData: ', transformedData);
                    console.log('📞 TODAY VoLTE Calls: ', volteValue);
                } else {
                    // DATE-RANGE → object format
                    transformedData = getDateRangeDataTransformResponse(apiData);

                    // ✅ Extract VoLTE from object for date range
                    volteValue = extractVoLTEFromObject(apiData);
                    console.log('✅ RANGE transformedData: ', transformedData);
                    console.log('📞 RANGE VoLTE Calls: ', volteValue);
                }

                // Dispatch VoLTE value to Redux
                dispatch(setVolteCallsCount(volteValue));

                setTableData(Array.isArray(transformedData) ? transformedData : []);
            } catch (error) {
                console.error('❌ Error transforming API data:', error);
                setTableData([]);
                dispatch(setVolteCallsCount(null));
            }
        } else if (!isLoadingData) {
            setTableData([]);
            dispatch(setVolteCallsCount(null));
        }
    }, [isLoadingData, dataSuccess, apiData, dateSelectionMode, selectedVerticleTab, dispatch]);

    useEffect(() => {
        if (!comparisonEnabled || !comparisonDates?.start?._d || !comparisonDates?.end?._d) {
            return;
        }

        const site = ['su', 'caesars', '5g'][selectedVerticleTab] ?? 'su';
        const dbDates = formatDateTimeForDBQuery(comparisonDates, selectedVerticleTab);
        getComparisonData({
            site,
            start: dbDates.start,
            end: dbDates.end,
        });
    }, [comparisonEnabled, comparisonDates, selectedVerticleTab, getComparisonData]);

    useEffect(() => {
        if (!comparisonEnabled || !comparisonDates?.start?._d || !comparisonDates?.end?._d) {
            setComparisonTableData([]);
            setComparisonVolteCallsCount(null);
            return;
        }

        if (comparisonDataSuccess && !isLoadingComparisonData) {
            try {
                const transformedData = getDateRangeDataTransformResponse(comparisonApiData);
                setComparisonTableData(Array.isArray(transformedData) ? transformedData : []);
                setComparisonVolteCallsCount(extractVoLTEFromObject(comparisonApiData));
            } catch (error) {
                console.error('Error transforming comparison data:', error);
                setComparisonTableData([]);
                setComparisonVolteCallsCount(null);
            }
        } else if (!isLoadingComparisonData) {
            setComparisonTableData([]);
            setComparisonVolteCallsCount(null);
        }
    }, [comparisonEnabled, comparisonDates, comparisonDataSuccess, isLoadingComparisonData, comparisonApiData]);

    // AUTO-REFRESH: Refresh table data every 10 minutes (only when autoRefresh is enabled)
    useEffect(() => {
        if (!autoRefresh) {
            if (autoRefreshIntervalRef.current) {
                clearInterval(autoRefreshIntervalRef.current);
            }
            return;
        }

        const refreshTableData = () => {
            const site = ['su', 'caesars', '5g'][selectedVerticleTab] ?? 'su';
            if (dateSelectionMode === 'today') {
                getData({ site });
            } else if (dateSelectionMode === 'range' && selectedDates?.start?._d && selectedDates?.end?._d) {
                const dbDates = formatDateTimeForDBQuery(selectedDates, selectedVerticleTab);
                getData({ site, start: dbDates.start, end: dbDates.end });
            }

            if (comparisonEnabled && comparisonDates?.start?._d && comparisonDates?.end?._d) {
                const comparisonDbDates = formatDateTimeForDBQuery(comparisonDates, selectedVerticleTab);
                getComparisonData({ site, start: comparisonDbDates.start, end: comparisonDbDates.end });
            }
        };

        autoRefreshIntervalRef.current = setInterval(refreshTableData, 600000); // 10 minutes

        return () => {
            if (autoRefreshIntervalRef.current) {
                clearInterval(autoRefreshIntervalRef.current);
            }
        };
    }, [selectedVerticleTab, dateSelectionMode, selectedDates, comparisonEnabled, comparisonDates, autoRefresh, getData, getComparisonData]);

    useEffect(() => {
        const handleScroll = () => {
            const headerOffset = 120;
            const scrollPosition = window.pageYOffset + headerOffset + 50;

            const cardsTop = cardsRef.current?.offsetTop ?? null;
            const outagesTop = outagesRef.current?.offsetTop ?? null;
            const tableTop = tableRef.current?.offsetTop ?? null;

            if (tableTop !== null && scrollPosition >= tableTop) {
                setActiveTab("userCounts");
            } else if (outagesTop !== null && scrollPosition >= outagesTop) {
                setActiveTab("outages");
            } else if (cardsTop !== null && scrollPosition >= cardsTop) {
                setActiveTab("alarms");
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);


    const scrollToRef = (ref) => {
        if (ref && ref.current) {
            const headerOffset = 120;
            const elementPosition = ref.current.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
        }
    };

    const handleTabChange = (tab) => {
        if (tab === "alarms" && cardsRef.current) {
            scrollToRef(cardsRef);
        } else if (tab === "userCounts" && tableRef.current) {
            scrollToRef(tableRef);
        } else if (tab === "outages" && tableRef.current) {
            scrollToRef(outagesRef);
        }
    };

    const getTodayDate = () => {
        const today = new Date();
        return today.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const handleSelectToday = () => {
        setDateSelectionMode('today');
        setComparisonEnabled(false);
        setComparisonDates(null);
        const today = {
            start: { _d: new Date() },
            end: { _d: new Date() }
        };
        dispatch(setSelectedDates(today));
        // REMOVED: getData call — now handled by useEffect
    };

    const handleDateRangeChange = (dates) => {
        console.log("Raw dates from picker:", dates);

        if (!dates?.start?._d || !dates?.end?._d) {
            console.warn("Invalid dates - missing _d");
            return;
        }

        setDateSelectionMode('range');
        dispatch(setSelectedDates(dates));
        // REMOVED: getData call — now handled by useEffect
    };

    const handleComparisonToggle = () => {
        if (comparisonEnabled) {
            setComparisonEnabled(false);
            setComparisonDates(null);
            return;
        }

        setComparisonEnabled(true);
    };

    const handleComparisonDateRangeChange = (dates) => {
        if (!dates?.start?._d || !dates?.end?._d) {
            return;
        }

        setComparisonDates(dates);
    };

    // ── Maintenance banner ─────────────────────────────────────────────────
    const { data: maintenanceData } = useGetMaintenanceMessageQuery(undefined, { pollingInterval: 300000 });
    const DISMISS_KEY = `maintenance_dismissed_${getUpcomingWednesdayLabel()}`;
    const [bannerDismissed, setBannerDismissed] = useState(
        () => sessionStorage.getItem(DISMISS_KEY) === 'true'
    );
    const handleDismissBanner = () => {
        sessionStorage.setItem(DISMISS_KEY, 'true');
        setBannerDismissed(true);
    };
    // Banner shows only when: inside the maintenance window AND a note was posted this week AND times are set AND not dismissed
    const messageDate = maintenanceData?.message?.published_date || maintenanceData?.message?.created_at;
    const showMaintenance = maintenanceData?.active && 
                            maintenanceData?.message?.maintenance_start_time && 
                            maintenanceData?.message?.maintenance_end_time && 
                            isDateInCurrentWeek(messageDate) &&
                            !bannerDismissed;
    const startTime = maintenanceData?.message?.maintenance_start_time ? formatTime24to12(maintenanceData.message.maintenance_start_time) : "7:00 AM";
    const endTime = maintenanceData?.message?.maintenance_end_time ? formatTime24to12(maintenanceData.message.maintenance_end_time) : "9:00 AM";
    const maintenanceMessage = `Maintenance is scheduled on ${getUpcomingWednesdayLabel()} between ${startTime} and ${endTime} EST. During this time period you may experience issues accessing the site or the data within.`;
    // ──────────────────────────────────────────────────────────────────────────

    const countPollingInterval = autoRefresh ? 60000 : 0;
    const { data: countData, isFetching: loadingCountData } = useGetCountDataQuery({ site: "su" }, { pollingInterval: countPollingInterval });
    const { data: caesarsCountData, isFetching: caesarsLoadingCountData } = useGetCountDataQuery({ site: "caesars" }, { pollingInterval: countPollingInterval });
    const { data: windsorCountData, isFetching: windsorLoadingCountData } = useGetCountDataQuery({ site: "windsor" }, { pollingInterval: countPollingInterval });
    const { data: verizonCountData, isFetching: verizonLoadingCountData } = useGetCountDataQuery({ site: "verizon" }, { pollingInterval: countPollingInterval });
    const { data: tmobileCountData, isFetching: tmobileLoadingCountData } = useGetCountDataQuery({ site: "tmobile" }, { pollingInterval: countPollingInterval });
    const { data: factoryCountData, isFetching: factoryLoadingCountData } = useGetCountDataQuery({ site: "5gfactory" }, { pollingInterval: countPollingInterval });
    const { data: outageData, isFetching: loadingoutageData } = useGetOutageDataQuery();
    console.log('outageData: ', outageData);
    const { data: availabilityData } = useGetAvailabilityQuery();

    // Report empty alarm count data
    const countErrorReported = useRef(false);
    const caesarsCountErrorReported = useRef(false);
    useEffect(() => {
        if (!loadingCountData && Array.isArray(countData) && countData.length === 0 && !countErrorReported.current) {
            countErrorReported.current = true;
            reportError({ page: "Dashboard", detail: "SU alarm count returned empty from /getAlarmsCount/" });
        }
    }, [loadingCountData, countData]);
    useEffect(() => {
        if (!caesarsLoadingCountData && Array.isArray(caesarsCountData) && caesarsCountData.length === 0 && !caesarsCountErrorReported.current) {
            caesarsCountErrorReported.current = true;
            reportError({ page: "Dashboard", detail: "Caesars alarm count returned empty from /getAlarmsCount/" });
        }
    }, [caesarsLoadingCountData, caesarsCountData]);
    // console.log("availabilityData", availabilityData)

    return (
        <>
            {showMaintenance && (
                <Box
                    sx={{
                        width: "100%",
                        backgroundColor: "#c62828",
                        color: "#ffffff",
                        py: 1.25,
                        px: { xs: 2, sm: 4 },
                        position: "fixed",
                        top: "3.5rem",
                        zIndex: 1200,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                    }}
                >
                    <NotificationsIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: "Montserrat", fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.95rem" }, lineHeight: 1.4, flex: 1 }}>
                        {maintenanceMessage}
                    </Typography>
                    <IconButton
                        onClick={handleDismissBanner}
                        size="small"
                        aria-label="Dismiss maintenance banner"
                        sx={{ color: "#ffffff", flexShrink: 0, ml: "auto", '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}
            <Box
                sx={{
                    width: "100%",
                    position: 'fixed',
                    top: showMaintenance ? { xs: "calc(3.5rem + 2.5rem)", sm: "calc(3.5rem + 2.6rem)" } : { xs: "2.5rem", sm: "3.1rem" },
                    backgroundColor: 'white',
                    zIndex: 1,
                    flexDirection: "row",
                    display: 'flex',
                    borderBottom: "1px solid #0000001F",
                    alignItems: "center",
                    paddingTop: { xs: "0.5rem", sm: "0" },
                    transition: "top 0.2s ease",
                }}
            >
                <TabNavigation onChange={handleTabChange} selectedTab={activeTab} features={displayedFeatures} />
            </Box>

            {/* Auto Refresh toggle — fixed right side, vertically aligned with tab bar */}
            <Tooltip title={autoRefresh ? "Auto Refresh: ON" : "Auto Refresh: OFF"} placement="bottom">
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                        position: 'fixed',
                        top: showMaintenance ? { xs: "calc(2.5rem + 2.5rem)", sm: "calc(3.1rem + 2.6rem)" } : { xs: "2.5rem", sm: "3.1rem" },
                        right: "1rem",
                        height: "3.8rem",
                        zIndex: 2,
                        transition: "top 0.2s ease",
                    }}
                >
                    <AutorenewIcon sx={{ fontSize: '16px', color: autoRefresh ? '#005AA6' : '#00000060' }} />
                    <Typography sx={{ fontSize: '12px', color: autoRefresh ? '#005AA6' : '#00000060', fontWeight: 500, display: { xs: 'none', sm: 'block' } }}>
                        Auto Refresh
                    </Typography>
                    <Switch
                        checked={autoRefresh}
                        onChange={(e) => dispatch(setAutoRefresh(e.target.checked))}
                        size="small"
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#005AA6' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#005AA6' },
                        }}
                    />
                </Stack>
            </Tooltip>

            <Stack sx={{
                marginTop: showMaintenance ? { xs: "7.5rem", sm: "7.5rem" } : { xs: "5rem", sm: "5rem" },
                width: "auto",
                padding: { xs: "0.5rem", sm: "0" },
                transition: "margin-top 0.2s ease",
            }}>
                {isEditing && allowedFeatures.length > 0 && (
                    <Box
                        role="group"
                        aria-label="Home page features"
                        sx={{
                            mt: "1rem",
                            mx: { xs: 0, sm: "1.3vw" },
                            px: 2,
                            py: 1,
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            columnGap: 2,
                            border: "1px dashed #005AA6",
                            borderRadius: "8px",
                            backgroundColor: "#E8F1FA",
                        }}
                    >
                        <Typography sx={{ fontFamily: "Montserrat", fontWeight: 700, color: "#0a2240", fontSize: "14px", mr: 1 }}>
                            Home page features:
                        </Typography>
                        {allowedFeatures.map(feature => (
                            <FormControlLabel
                                key={feature.key}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={homeDraft.has(feature.key)}
                                        onChange={(e) => toggleHomeKeys([feature.key], e.target.checked)}
                                        sx={{ '&.Mui-checked': { color: '#005AA6' } }}
                                    />
                                }
                                label={feature.label}
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', color: '#0a2240' } }}
                            />
                        ))}
                    </Box>
                )}
                {hasFeature("alarms") && (
                    <Box sx={{ width: "100%", ...featureSectionSx("alarms") }} ref={cardsRef}>
                        <Typography
                            sx={{
                                paddingLeft: { xs: "0.3rem", sm: "1.3vw" },
                                fontFamily: "Montserrat",
                                fontWeight: "700",
                                color: "#0a2240",
                                fontSize: "20px",
                                mt: "1rem"
                            }}
                        >
                            Alarms
                        </Typography>
                        {loadingCountData || caesarsLoadingCountData || verizonLoadingCountData || tmobileLoadingCountData || windsorLoadingCountData || factoryLoadingCountData ? (
                            <AlarmCardsLoadingSkeleton />
                        ) : (
                            <AlarmCardsContent
                                data={countData?.alarms}
                                caesarsCountData={caesarsCountData?.alarms}
                                verizonCountData={verizonCountData?.alarms}
                                tmobileCountData={tmobileCountData?.alarms}
                                windsorCountData={windsorCountData?.alarms}
                                factoryCountData={factoryCountData?.alarms}
                            />
                        )}
                    </Box>
                )}
                {hasFeature("outages") && (
                    <Box sx={{ width: "100%", marginTop: { xs: "3rem", sm: "5rem" }, ...featureSectionSx("outages") }} ref={outagesRef}>
                        {/* <Typography
                            sx={{
                                paddingLeft: { xs: "0.3rem", sm: "1.3vw" },
                                fontFamily: "Montserrat",
                                fontWeight: "700",
                                color: "#0a2240",
                                fontSize: "20px",
                                mt: "1rem"
                            }}
                        >
                            Outages
                        </Typography> */}
                        {loadingoutageData ? (
                            <AlarmCardsLoadingSkeleton />
                        ) : (
                            <OutagesContent outageData={outageData} />
                        )}
                    </Box>
                )}


                {hasFeature("user_counts") && (
                    <Box sx={{
                        marginTop: { xs: "3rem", sm: "5rem" },
                        padding: { xs: "0 0rem", sm: "0" },
                        ...featureSectionSx("user_counts"),
                    }} ref={tableRef}>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                        >
                            <Typography
                                sx={{
                                    paddingLeft: { xs: "0rem", sm: "1.3vw" },
                                    fontFamily: "Montserrat",
                                    fontWeight: "700",
                                    color: "#0a2240",
                                    fontSize: "20px",
                                    marginBottom: { xs: "1rem", sm: "0" },
                                }}
                            >
                                User Counts & Data Usage
                            </Typography>
                            <Stack
                                direction={{ xs: "column", sm: "column", md: "row", lg: "row", xl: "row" }}
                                sx={{
                                    alignItems: { xs: "flex-start", sm: "flex-start", md: "flex-start", lg: "center", xl: "center" },
                                    marginBottom: { xs: "1rem", sm: "0px", md: "0px" },
                                    width: { xs: "100%", sm: "auto" },
                                    paddingRight: { xs: "0rem", sm: "1.5vw", md: "1rem", lg: "1rem", xl: "1rem" },
                                }}
                            >
                                {/* <Chip
                                    label={selectedVerticleTab === 1 ? 'All Times in PST' : 'All Times in EST'}
                                    sx={{
                                        marginRight: "1rem",
                                        fontWeight: "600",
                                        marginBottom: { xs: "1rem", sm: "1rem", md: "0px", lg: "0px", xl: "0px" }
                                    }}
                                /> */}

                                <Stack direction={'row'}>
                                    <InfoIcon color="#0a2240" sx={{ marginRight: "0.5rem", fontSize: "20px" }} />
                                    <Typography sx={{
                                        marginRight: "1rem",
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        marginBottom: { xs: "1rem", sm: "1rem", md: "0px", lg: "0px", xl: "0px" }
                                    }}>
                                        {selectedVerticleTab === 1 ? 'All Times in PST' : 'All Times in EST'}
                                    </Typography>
                                </Stack>




                                <Stack spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        alignItems={{ xs: "stretch", md: "center" }}
                                        spacing={1}
                                        sx={{ width: "100%" }}
                                    >
                                        <Stack direction="row" sx={{ width: { xs: "100%", md: "auto" } }}>
                                            <SelectTodayButton
                                                active={dateSelectionMode === 'today'}
                                                onClick={handleSelectToday}
                                                sx={{
                                                    width: { xs: "50%", sm: "auto" },
                                                    fontSize: { xs: "12px", sm: "14px" },
                                                    padding: { xs: "6px 8px", sm: "8px 16px" }
                                                }}
                                            >
                                                {`Today: ${getTodayDate()}`}
                                            </SelectTodayButton>

                                            <MuiDateRangePicker
                                                isActive={dateSelectionMode === 'range'}
                                                onChange={handleDateRangeChange}
                                                dateSelectionMode={dateSelectionMode}
                                                selectedVerticleTab={selectedVerticleTab}
                                            />
                                        </Stack>

                                        <Tooltip title={dateSelectionMode === 'range' ? '' : 'Select a date range to compare'}>
                                            <Box component="span" sx={{ display: 'inline-flex', width: { xs: '100%', md: 'auto' } }}>
                                                <Button
                                                    variant={comparisonEnabled ? "contained" : "outlined"}
                                                    startIcon={<CompareArrowsIcon />}
                                                    disabled={dateSelectionMode !== 'range'}
                                                    onClick={handleComparisonToggle}
                                                    sx={{
                                                        width: { xs: '100%', md: 'auto' },
                                                        height: 40,
                                                        borderRadius: '4px',
                                                        textTransform: 'none',
                                                        whiteSpace: 'nowrap',
                                                        backgroundColor: comparisonEnabled ? '#0a2240' : 'white',
                                                        color: comparisonEnabled ? 'white' : '#005AA6',
                                                        borderColor: '#0000001F',
                                                        '&:hover': {
                                                            backgroundColor: comparisonEnabled ? '#0a2240' : 'rgba(10, 34, 64, 0.04)',
                                                            borderColor: '#0a2240',
                                                        },
                                                    }}
                                                >
                                                    Compare
                                                </Button>
                                            </Box>
                                        </Tooltip>
                                    </Stack>

                                    {comparisonEnabled && (
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            alignItems={{ xs: "flex-start", sm: "center" }}
                                            justifyContent="flex-end"
                                            spacing={1}
                                        >
                                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0a2240' }}>
                                                Compare with
                                            </Typography>
                                            <MuiDateRangePicker
                                                isActive={Boolean(comparisonDates?.start?._d && comparisonDates?.end?._d)}
                                                onChange={handleComparisonDateRangeChange}
                                                dateSelectionMode="range"
                                                selectedVerticleTab={selectedVerticleTab}
                                            />
                                            <Tooltip title="Close comparison">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Close comparison"
                                                    onClick={handleComparisonToggle}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    )}
                                </Stack>
                            </Stack>



                        </Stack>

                        {isLoadingData ? (
                            <TableLoadingSkeleton />
                        ) : (
                            <VerticalTabs
                                tableData={tableData || []}
                                dateSelectionMode={dateSelectionMode}
                                setSelectedVerticleTab={setSelectedVerticleTab}
                                selectedVerticleTab={selectedVerticleTab}
                                sitePerf={{ "syracyse": availabilityData?.Syracuse, "caesars": availabilityData?.Caesars, "jmahq": availabilityData?.["5G Factory"] }}
                                selectedDates={selectedDates}
                                volteCallsCount={volteCallsCount}
                                comparisonEnabled={comparisonEnabled}
                                comparisonDates={comparisonDates}
                                comparisonTableData={comparisonTableData}
                                comparisonVolteCallsCount={comparisonVolteCallsCount}
                                isLoadingComparisonData={isLoadingComparisonData}
                                comparisonDataError={comparisonDataError}
                            />
                        )}
                    </Box>
                )}

            </Stack >
        </>
    );
};